import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Panel, useStoreApi } from "@xyflow/react";
import { Rnd } from "react-rnd";
import { GripHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
const STORAGE_KEY = "bliks:process-editor-detail-panel";
const DRAG_CLASS = "floating-detail-panel-drag-handle";
const MIN_WIDTH = 280;
const MAX_WIDTH = 720;
// Floor for the panel's available height in a very short viewport, so the
// scroll cap never collapses to a sliver. Not a panel min-height — height is
// content-driven.
const MIN_AVAILABLE_HEIGHT = 220;
const VIEWPORT_MARGIN = 100;
const DEFAULT_PLACEMENT = {
    width: 400,
    right: 10,
    y: 40
};
const clamp = (v, lo, hi)=>Math.max(lo, Math.min(hi, v));
function placementOf(g, canvas) {
    return {
        width: g.width,
        right: canvas.width - g.x - g.width,
        y: g.y
    };
}
function writeStored(p) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
    } catch  {
    // quota or privacy mode
    }
}
function readStored(canvas) {
    if (typeof window === "undefined") return null;
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "null");
        if (!parsed || typeof parsed !== "object") return null;
        const { width, right, x, y } = parsed;
        if (typeof width !== "number" || typeof y !== "number") return null;
        const clampedWidth = clamp(width, MIN_WIDTH, MAX_WIDTH);
        if (typeof right === "number") return {
            width: clampedWidth,
            right,
            y
        };
        if (typeof x !== "number") return null;
        // Saved by its left edge before the right anchor, with no sidebar beside
        // the canvas. The canvas it opens on may be narrowed by one, so the
        // conversion is clamped and never saved: the next drag or resize saves.
        const converted = placementOf({
            width: clampedWidth,
            x,
            y
        }, canvas);
        return {
            ...converted,
            right: clamp(converted.right, 0, Math.max(0, canvas.width - clampedWidth))
        };
    } catch  {
        return null;
    }
}
// Available height for the panel before it should start scrolling internally.
const availableHeight = (rect)=>Math.max(MIN_AVAILABLE_HEIGHT, rect.height - VIEWPORT_MARGIN);
// Drawn, not stored: a canvas too small for the placement squeezes the panel
// in, and the placement returns once the canvas grows again.
function drawnGeometry({ canvas, placement }) {
    const { width, right, y } = placement;
    return {
        width,
        x: clamp(canvas.width - right - width, 0, Math.max(0, canvas.width - width)),
        // Height is auto, so we don't know the rendered bottom here — keep at least a
        // header strip on-canvas. react-rnd's bounds="parent" enforces the true,
        // content-aware floor during an actual drag.
        y: clamp(y, 0, Math.max(0, canvas.height - VIEWPORT_MARGIN))
    };
}
function place(layout, g) {
    const placement = placementOf(g, layout.canvas);
    writeStored(placement);
    return {
        ...layout,
        placement
    };
}
// re-resizable merges partial `enable` with an all-true default — every key
// must be set explicitly to disable the other handles. Only width (left) is
// resizable; height is content-driven so the hit-area matches the visible card.
const RESIZE_ENABLED = {
    left: true,
    bottom: false,
    bottomLeft: false,
    right: false,
    top: false,
    topLeft: false,
    topRight: false,
    bottomRight: false
};
const PANEL_WRAPPER_STYLE = {
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 0,
    pointerEvents: "none"
};
const RND_STYLE = {
    pointerEvents: "auto",
    display: "flex",
    flexDirection: "column"
};
function ResizeHandle({ label, className }) {
    return /*#__PURE__*/ _jsx("div", {
        "aria-label": label,
        title: label,
        className: `absolute bg-transparent hover:bg-muted-foreground/40 transition-colors ${className}`
    });
}
export function FloatingDetailPanel({ children, panelRef }) {
    const { t } = useTranslation();
    const [layout, setLayout] = useState(null);
    const initialized = useRef(false);
    const store = useStoreApi();
    const onParentRef = useCallback((node)=>{
        if (!node || initialized.current) return;
        initialized.current = true;
        const { width, height } = node.getBoundingClientRect();
        const canvas = {
            width,
            height
        };
        setLayout({
            canvas,
            placement: readStored(canvas) ?? DEFAULT_PLACEMENT
        });
    }, []);
    // External sync: follow React Flow's own measure of the canvas this panel's
    // parent fills, as the window resizes or a docked sidebar shrinks it.
    useEffect(function followCanvasSize() {
        return store.subscribe((state, prev)=>{
            if (state.width === prev.width && state.height === prev.height) return;
            const canvas = {
                width: state.width,
                height: state.height
            };
            setLayout((current)=>current && {
                    ...current,
                    canvas
                });
        });
    }, [
        store
    ]);
    const handleDragStop = useCallback((_e, d)=>{
        setLayout((prev)=>prev && place(prev, {
                width: prev.placement.width,
                x: d.x,
                y: d.y
            }));
    }, []);
    const handleResizeStop = useCallback((_e, _dir, ref, _delta, pos)=>{
        setLayout((prev)=>prev && place(prev, {
                width: ref.offsetWidth,
                x: pos.x,
                y: pos.y
            }));
    }, []);
    const geometry = layout && drawnGeometry(layout);
    const maxHeight = layout ? availableHeight(layout.canvas) : undefined;
    const resizeLabel = t("processFlow.detailPanel.dragToResize");
    const moveLabel = t("processFlow.detailPanel.dragToMove");
    return /*#__PURE__*/ _jsx(Panel, {
        position: "top-left",
        style: PANEL_WRAPPER_STYLE,
        children: /*#__PURE__*/ _jsx("div", {
            ref: onParentRef,
            className: "absolute inset-0 pointer-events-none",
            children: geometry && /*#__PURE__*/ _jsxs(Rnd, {
                position: {
                    x: geometry.x,
                    y: geometry.y
                },
                size: {
                    width: geometry.width,
                    height: "auto"
                },
                bounds: "parent",
                minWidth: MIN_WIDTH,
                maxWidth: MAX_WIDTH,
                maxHeight: maxHeight,
                enableResizing: RESIZE_ENABLED,
                dragHandleClassName: DRAG_CLASS,
                onDragStop: handleDragStop,
                onResizeStop: handleResizeStop,
                style: RND_STYLE,
                resizeHandleComponent: {
                    left: /*#__PURE__*/ _jsx(ResizeHandle, {
                        label: resizeLabel,
                        className: "top-1/2 -translate-y-1/2 -left-0.5 h-16 w-1 rounded-full"
                    })
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: `${DRAG_CLASS} shrink-0 self-center flex h-3 cursor-move items-center justify-center rounded-t-md bg-muted hover:bg-muted/80 px-3 transition-colors`,
                        "aria-label": moveLabel,
                        title: moveLabel,
                        children: /*#__PURE__*/ _jsx(GripHorizontal, {
                            className: "h-3 w-3 text-muted-foreground",
                            strokeWidth: 2
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        ref: panelRef,
                        className: "flex-1 min-h-0 flex flex-col",
                        children: children
                    })
                ]
            })
        })
    });
}

//# sourceMappingURL=FloatingDetailPanel.js.map